/* QMZ bulk order via Excel (per collection). Runs on collection pages that include the
   "QMZ Bulk Order" section. Generates a template (Production + Sample rows) with red
   conditional formatting when a product's Production total < 10 or Sample total > 2,
   parses the filled sheet, and adds everything to the cart by exact Variant ID. */
(function () {
  const HEADERS = ['Image', 'Product', 'Order Type', 'Variant', 'Quantity', 'Price', 'Variant ID', 'PKey', 'OTKey'];
  const COL_IMAGE = 1, COL_PRODUCT = 2, COL_OT = 3, COL_VARIANT = 4, COL_QTY = 5, COL_PRICE = 6, COL_ID = 7, COL_PKEY = 8, COL_OTKEY = 9;

  /* Fetch a product image and return { dataUrl, extension, width, height } for ExcelJS,
     or null on failure. Natural width/height let us size the picture to its own aspect
     ratio instead of stretching it to fill a cell. */
  async function fetchImage(url) {
    try {
      const res = await fetch(url);
      if (!res.ok) return null;
      const blob = await res.blob();
      const extension = blob.type === 'image/png' ? 'png' : 'jpeg';
      const dataUrl = await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(fr.result);
        fr.onerror = reject;
        fr.readAsDataURL(blob);
      });
      const dims = await new Promise((resolve) => {
        const im = new Image();
        im.onload = () => resolve({ w: im.naturalWidth, h: im.naturalHeight });
        im.onerror = () => resolve({ w: 0, h: 0 });
        im.src = dataUrl;
      });
      return { dataUrl: dataUrl, extension: extension, width: dims.w, height: dims.h };
    } catch (e) {
      return null;
    }
  }

  function variantLabel(product, variant) {
    const otIndex = product.options.findIndex((n) => String(n).toLowerCase() === 'order type');
    const parts = variant.values.filter((_, i) => i !== otIndex);
    const label = parts.filter((v) => v && String(v).toLowerCase() !== 'default title').join(' / ');
    return label || 'Default';
  }

  /* ---- lazy-load ExcelJS (vendored asset) ---- */
  let excelPromise = null;
  function loadExcel(root) {
    if (window.ExcelJS) return Promise.resolve(window.ExcelJS);
    if (excelPromise) return excelPromise;
    const src = root.getAttribute('data-exceljs');
    excelPromise = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = src;
      s.onload = () => (window.ExcelJS ? resolve(window.ExcelJS) : reject(new Error('ExcelJS missing')));
      s.onerror = () => reject(new Error('Failed to load spreadsheet engine'));
      document.head.appendChild(s);
    });
    return excelPromise;
  }

  function status(root, msg, kind) {
    const el = root.querySelector('[data-qmz-status]');
    if (!el) return;
    el.hidden = false;
    el.className = 'qmz-bulk__status qmz-bulk__status--' + (kind || 'info');
    el.textContent = msg;
  }

  function data(root) {
    const tag = root.querySelector('[data-qmz-bulk-data]');
    try { return JSON.parse(tag.textContent); } catch (e) { return null; }
  }

  function limits(root) {
    return {
      min: parseInt(root.getAttribute('data-min-qty'), 10) || 10,
      sampleMax: parseInt(root.getAttribute('data-sample-max'), 10) || 2,
    };
  }

  /* ---- Build & download the template ---- */
  async function download(root) {
    const btn = root.querySelector('[data-qmz-download]');
    btn.disabled = true;
    status(root, 'Preparing your order sheet…', 'info');
    try {
      const ExcelJS = await loadExcel(root);
      const d = data(root);
      const { min, sampleMax } = limits(root);
      if (!d || !d.products.length) { status(root, 'No products found in this collection.', 'error'); btn.disabled = false; return; }

      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet('Order', { views: [{ state: 'frozen', ySplit: 1 }] });
      ws.columns = [
        { header: HEADERS[0], width: 15 },
        { header: HEADERS[1], width: 34 },
        { header: HEADERS[2], width: 13 },
        { header: HEADERS[3], width: 24 },
        { header: HEADERS[4], width: 12 },
        { header: HEADERS[5], width: 12 },
        { header: HEADERS[6], width: 16 },
        { header: HEADERS[7], width: 10 },
        { header: HEADERS[8], width: 10 },
      ];

      const head = ws.getRow(1);
      head.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      head.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF08263A' } };
      head.alignment = { vertical: 'middle' };

      const ROW_H = 20;
      const money = (paise) => (paise == null ? null : Math.round(paise) / 100);
      const PRICE_FMT = '"₹"#,##0.00';
      let r = 2;
      const productRows = []; // { image, startRow, endRow } for merged per-product images
      d.products.forEach((p) => {
        const startRow = r;
        // Production rows first, then Sample, then anything untyped.
        const order = { Production: 0, Sample: 1, '': 2 };
        const vs = p.variants.slice().sort((a, b) => (order[a.orderType] ?? 3) - (order[b.orderType] ?? 3));

        // Ordered rows for this product: real variants, then Sample size-set helper rows.
        const rowsData = vs.map((v) => ({
          orderType: v.orderType || '', variant: variantLabel(p, v), price: v.price, id: v.id, set: false,
        }));

        // Size-set helper rows (Sample): client puts a number of SETS in Quantity; the parser
        // expands 1 set -> 1 Sample piece per size. Actual Sample variant ids (+ per-set price)
        // are embedded so matching works for any option layout (e.g. products with a Title option).
        const sizeIdx = p.options.findIndex((n) => String(n).toLowerCase() === 'size');
        const sampleBySize = {};
        if (sizeIdx >= 0) {
          p.variants.forEach((v) => {
            if (String(v.orderType).toLowerCase() === 'sample' && v.available !== false) {
              const sz = String(v.values[sizeIdx] || '').toUpperCase().replace(/\s+/g, '');
              if (sz && !(sz in sampleBySize)) sampleBySize[sz] = { id: v.id, price: v.price };
            }
          });
        }
        const SET_DEF = { FULL: ['S', 'M', 'L', 'XL', '2XL'], HALF: ['S', 'L', '2XL'] };
        [['FULL', 'Full Size Set (S–2XL) — enter number of sets'],
         ['HALF', 'Half Size Set (S, L, 2XL) — enter number of sets']].forEach((sd) => {
          const parts = SET_DEF[sd[0]].map((sz) => sampleBySize[sz]).filter(Boolean);
          if (!parts.length) return;
          rowsData.push({
            orderType: 'Sample',
            variant: sd[1],
            price: parts.reduce((s, x) => s + (x.price || 0), 0),
            id: 'SET:' + sd[0] + ':' + parts.map((x) => x.id).join(','),
            set: true,
          });
        });

        // Write rows, merging Product + Order Type down each consecutive same-type run.
        let i = 0;
        while (i < rowsData.length) {
          const runStart = r;
          const ot = rowsData[i].orderType;
          let j = i;
          while (j < rowsData.length && rowsData[j].orderType === ot) {
            const rd = rowsData[j];
            const row = ws.getRow(r);
            row.height = ROW_H;
            // Merged cells keep only the top-left value, so write Product/Order Type once per run.
            if (r === runStart) {
              row.getCell(COL_PRODUCT).value = p.title;
              row.getCell(COL_OT).value = ot;
            }
            row.getCell(COL_VARIANT).value = rd.variant;
            row.getCell(COL_QTY).value = null;
            row.getCell(COL_QTY).dataValidation = {
              type: 'whole', operator: 'greaterThanOrEqual', formulae: [0], allowBlank: true,
            };
            row.getCell(COL_PRICE).value = money(rd.price);
            row.getCell(COL_PRICE).numFmt = PRICE_FMT;
            row.getCell(COL_ID).value = rd.id;
            // Hidden per-row helpers so the conditional-format SUMIFS still works under merges.
            row.getCell(COL_PKEY).value = p.title;
            row.getCell(COL_OTKEY).value = ot;
            row.getCell(COL_PRODUCT).alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
            row.getCell(COL_OT).alignment = { vertical: 'middle', horizontal: 'center' };
            row.getCell(COL_VARIANT).alignment = { vertical: 'middle', horizontal: 'left' };
            row.getCell(COL_QTY).alignment = { vertical: 'middle', horizontal: 'center' };
            row.getCell(COL_PRICE).alignment = { vertical: 'middle', horizontal: 'right' };
            row.getCell(COL_ID).alignment = { vertical: 'middle', horizontal: 'center' };
            if (rd.set) row.getCell(COL_VARIANT).font = { italic: true, color: { argb: 'FF6B7280' } };
            r++;
            j++;
          }
          const runEnd = r - 1;
          if (runEnd > runStart) {
            ws.mergeCells(runStart, COL_PRODUCT, runEnd, COL_PRODUCT);
            ws.mergeCells(runStart, COL_OT, runEnd, COL_OT);
          }
          i = j;
        }
        if (r > startRow) productRows.push({ image: p.image, startRow: startRow, endRow: r - 1 });
      });
      const last = r - 1;
      ws.getColumn(COL_ID).hidden = true;
      ws.getColumn(COL_PKEY).hidden = true;
      ws.getColumn(COL_OTKEY).hidden = true;

      // One product image per product, floating over the image cell that's merged across
      // all of the product's variant rows. Sized to the picture's own aspect ratio and
      // capped in height so tall products (many variants) don't get stretched.
      const PX_PER_ROW = ROW_H * 96 / 72;          // Excel row height (points) -> pixels
      const COL_IMG_PX = 100;                       // usable width of the width:15 image column
      const MAX_IMG_ROWS = 5;                        // never taller than ~5 rows
      await Promise.all(
        productRows.map(async (pr) => {
          if (!pr.image) return;
          if (pr.endRow > pr.startRow) ws.mergeCells(pr.startRow, COL_IMAGE, pr.endRow, COL_IMAGE);
          const img = await fetchImage(pr.image);
          if (!img) return;
          const imageId = wb.addImage({ base64: img.dataUrl, extension: img.extension });
          const rowSpan = pr.endRow - pr.startRow + 1;
          const availH = Math.min(rowSpan, MAX_IMG_ROWS) * PX_PER_ROW;
          const nw = img.width || COL_IMG_PX, nh = img.height || availH;
          const scale = Math.min(COL_IMG_PX / nw, availH / nh);
          const width = Math.max(1, Math.round(nw * scale));
          const height = Math.max(1, Math.round(nh * scale));
          // One-cell anchor + explicit pixel size => floats over the merged cell, moves
          // with it, but keeps a fixed (non-stretched) size.
          ws.addImage(imageId, {
            tl: { col: COL_IMAGE - 1, row: pr.startRow - 1 },
            ext: { width: width, height: height },
            editAs: 'oneCell',
          });
        })
      );

      // Keep every cell editable (client fills quantities), but protect the sheet so the
      // images are locked in their cells — a client can't drag them out without first
      // un-protecting the sheet (Review → Unprotect Sheet).
      for (let rr = 1; rr <= last; rr++) {
        const row = ws.getRow(rr);
        for (let cc = 1; cc <= COL_OTKEY; cc++) row.getCell(cc).protection = { locked: false };
      }
      await ws.protect('', {
        // objects/scenarios false => LOCKED (images can't be moved). Everything else
        // true => allowed, so all cells stay fully editable for the client.
        objects: false,
        scenarios: false,
        selectLockedCells: true,
        selectUnlockedCells: true,
        formatCells: true,
        formatColumns: true,
        formatRows: true,
        insertColumns: true,
        insertRows: true,
        insertHyperlinks: true,
        deleteColumns: true,
        deleteRows: true,
        sort: true,
        autoFilter: true,
        pivotTables: true,
      });

      // Conditional formatting on the Quantity column (E):
      //  - Production cell red when that product's Production total < min (10)
      //  QMZ 2026-07-28: Sample per-product cap DISABLED — no Sample red rule anymore.
      //  Product (B) and Order Type (C) are now MERGED for display, so the SUMIFS reads the
      //  hidden per-row helper columns PKey (H) and OTKey (I) instead.
      if (last >= 2) {
        const redFill = { fill: { type: 'pattern', pattern: 'solid', bgColor: { argb: 'FFFFC7CE' } }, font: { color: { argb: 'FF9C0006' } } };
        ws.addConditionalFormatting({
          ref: `E2:E${last}`,
          rules: [
            {
              type: 'expression',
              priority: 1,
              formulae: [`AND($H2<>"",$I2="Production",SUMIFS($E$2:$E$${last},$H$2:$H$${last},$H2,$I$2:$I$${last},"Production")<${min})`],
              style: redFill,
            },
          ],
        });
      }

      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `QMZ Order - ${d.collection} - template.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
      status(root, 'Template downloaded. Fill in the Quantity column and upload it back here.', 'success');
    } catch (e) {
      status(root, 'Could not build the template: ' + e.message, 'error');
    }
    btn.disabled = false;
  }

  /* ---- Parse the uploaded sheet ---- */
  async function parse(root, file) {
    status(root, 'Reading your sheet…', 'info');
    const ExcelJS = await loadExcel(root);
    const wb = new ExcelJS.Workbook();
    await wb.xlsx.load(await file.arrayBuffer());
    const ws = wb.worksheets[0];
    if (!ws) throw new Error('The file has no sheets.');

    const header = ws.getRow(1);
    let cId = COL_ID, cQty = COL_QTY, cProd = COL_PRODUCT, cVar = COL_VARIANT, cOt = COL_OT;
    header.eachCell((cell, col) => {
      const t = String(cell.value || '').trim().toLowerCase();
      if (t === 'variant id') cId = col;
      else if (t === 'quantity') cQty = col;
      else if (t === 'product') cProd = col;
      else if (t === 'variant') cVar = col;
      else if (t === 'order type') cOt = col;
    });

    // Collect rows. A size-set row's (hidden) Variant ID is "SET:FULL:id,id,…" — its Quantity
    // is a number of SETS, expanded to that many pieces of each embedded Sample variant id.
    // Product and Order Type are MERGED for display, so their covered cells read blank — carry
    // the last-seen value down rather than treating a blank as end-of-data.
    const rows = [];
    let lastProduct = '', lastOt = '';
    ws.eachRow((row, n) => {
      if (n === 1) return;
      let qty = row.getCell(cQty).value;
      if (qty && typeof qty === 'object' && 'result' in qty) qty = qty.result;
      let product = String(row.getCell(cProd).value || '').trim();
      let orderType = String(row.getCell(cOt).value || '').trim();
      if (product) lastProduct = product; else product = lastProduct;
      if (orderType) lastOt = orderType; else orderType = lastOt;
      rows.push({
        idRaw: String(row.getCell(cId).value || '').trim(),
        qty: parseInt(qty, 10),
        product: product,
        label: String(row.getCell(cVar).value || '').trim(),
        orderType: orderType,
      });
    });

    // Expand SET rows + real rows; merge duplicate variant ids.
    const byId = {};
    const push = (id, quantity, product, label, orderType) => {
      if (byId[id]) { byId[id].quantity += quantity; return; }
      byId[id] = { id, quantity, product, label, orderType };
    };
    rows.forEach((row) => {
      if (!row.qty || row.qty <= 0) return;
      const setMatch = /^SET:(FULL|HALF):(.+)$/i.exec(row.idRaw);
      if (setMatch) {
        setMatch[2].split(',').forEach((s) => {
          const vid = parseInt(s, 10);
          if (vid) push(vid, row.qty, row.product, 'Size set', 'Sample');
        });
        return;
      }
      const idNum = parseInt(row.idRaw, 10);
      if (!idNum) return;
      push(idNum, row.qty, row.product, row.label, row.orderType);
    });
    const items = Object.keys(byId).map((k) => byId[k]);
    return items;
  }

  function renderPreview(root, items) {
    const { min, sampleMax } = limits(root);
    const wrap = root.querySelector('[data-qmz-preview]');
    if (!items.length) {
      wrap.hidden = true;
      status(root, 'No quantities found in the sheet. Fill in the Quantity column and try again.', 'error');
      return;
    }

    // Per product + order type totals.
    const prodTotals = {}, sampTotals = {};
    items.forEach((i) => {
      if (/sample/i.test(i.orderType)) sampTotals[i.product] = (sampTotals[i.product] || 0) + i.quantity;
      else prodTotals[i.product] = (prodTotals[i.product] || 0) + i.quantity;
    });
    const lowProd = Object.keys(prodTotals).filter((p) => prodTotals[p] < min);
    // QMZ 2026-07-28: Sample per-product cap DISABLED — no high-sample warning/flag.
    function isLow(i) {
      return /sample/i.test(i.orderType) ? false : prodTotals[i.product] < min;
    }

    const totalPieces = items.reduce((s, i) => s + i.quantity, 0);
    const products = new Set(items.map((i) => i.product)).size;

    let html = `<p><strong>${items.length}</strong> lines · <strong>${totalPieces}</strong> pieces · <strong>${products}</strong> products.</p>`;
    if (lowProd.length) {
      html += `<p class="qmz-bulk__red">⚠ Production under ${min}: ${lowProd.map((p) => `${escapeHtml(p)} (${prodTotals[p]})`).join(', ')}. Checkout stays blocked until each is at least ${min}.</p>`;
    }
    html += '<div class="qmz-bulk__preview-scroll"><table><thead><tr><th>Product</th><th>Type</th><th>Variant</th><th>Qty</th></tr></thead><tbody>';
    items.forEach((i) => {
      const samp = /sample/i.test(i.orderType);
      const chip = i.orderType
        ? `<span class="qmz-bulk__ot ${samp ? 'qmz-bulk__ot--samp' : 'qmz-bulk__ot--prod'}">${escapeHtml(i.orderType)}</span>`
        : '';
      html += `<tr class="${isLow(i) ? 'qmz-bulk__row--low' : ''}"><td>${escapeHtml(i.product)}</td><td>${chip}</td><td>${escapeHtml(i.label)}</td><td>${i.quantity}</td></tr>`;
    });
    html += '</tbody></table></div>';
    html += '<div class="qmz-bulk__confirm-row">'
      + '<button type="button" class="qmz-bulk__confirm" data-qmz-confirm="replace">Replace cart with this order</button>'
      + '<button type="button" class="qmz-bulk__confirm" style="background:#08263a" data-qmz-confirm="add">Add to current cart</button>'
      + '</div>';
    wrap.innerHTML = html;
    wrap.hidden = false;

    wrap.querySelectorAll('[data-qmz-confirm]').forEach((b) => {
      b.addEventListener('click', () => addToCart(root, items, b.getAttribute('data-qmz-confirm') === 'replace'));
    });
    status(root, 'Review your order below, then choose how to add it to the cart.', 'info');
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }

  async function addToCart(root, items, replace) {
    root.querySelectorAll('[data-qmz-confirm]').forEach((b) => (b.disabled = true));
    status(root, replace ? 'Clearing cart and adding your order…' : 'Adding your order to the cart…', 'info');
    try {
      if (replace) {
        await fetch('/cart/clear.js', { method: 'POST', headers: { 'Content-Type': 'application/json' } });
      }
      const payload = items.map((i) => ({ id: i.id, quantity: i.quantity }));
      for (let i = 0; i < payload.length; i += 100) {
        const chunk = payload.slice(i, i + 100);
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: chunk }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.description || 'Some items could not be added.');
        }
      }
      status(root, 'Order added! Taking you to your cart…', 'success');
      window.location.href = '/cart';
    } catch (e) {
      status(root, 'Could not add to cart: ' + e.message, 'error');
      root.querySelectorAll('[data-qmz-confirm]').forEach((b) => (b.disabled = false));
    }
  }

  /* ---- wire up ---- */
  function init(root) {
    if (root.__qmzBulkInit) return;
    root.__qmzBulkInit = true;
    root.querySelector('[data-qmz-download]')?.addEventListener('click', () => download(root));
    const fileInput = root.querySelector('[data-qmz-file]');
    root.querySelector('[data-qmz-upload-trigger]')?.addEventListener('click', () => fileInput?.click());
    fileInput?.addEventListener('change', async (e) => {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      try {
        const items = await parse(root, file);
        renderPreview(root, items);
      } catch (err) {
        status(root, 'Could not read the sheet: ' + err.message, 'error');
      }
      e.target.value = '';
    });
  }

  function boot() {
    document.querySelectorAll('[data-qmz-bulk]').forEach(init);
  }
  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);
})();
